import FeatureTemplate from "../../components/FeatureTemplate";

const PayrollComplianceAutomation = () => {
  return (
    <FeatureTemplate 
      title="Payroll & Compliance Automation" 
      description="AI-driven payroll with real-time compliance updates and automated statutory filings"
      featureId="payroll-compliance-automation"
    >
      <p className="mb-6">
        Our advanced Payroll & Compliance Automation system combines intelligent payroll processing with real-time regulatory compliance to ensure 100% accuracy in salary calculations, tax deductions, and statutory filings. The platform leverages AI to automatically adapt to changes in Indian labor laws, tax regulations, and social security requirements, eliminating compliance risks and administrative burden.
      </p>
      
      <p className="mb-6">
        The AI-driven payroll engine processes over 100 payroll components including basic salary, allowances, deductions, bonuses, and variable pay with mathematical precision. Our proprietary compliance engine continuously monitors 50+ government portals and regulatory bodies, automatically updating the system with changes in PF, ESI, PT, TDS, GST, and labor law regulations before they take effect.
      </p>
      
      <p className="mb-6">
        With one-click payroll processing, the system generates accurate salary calculations, processes bank transfers via NEFT/RTGS/IMPS, distributes digital payslips, and files all statutory returns automatically. The platform provides real-time compliance alerts and audit-ready documentation for complete transparency and risk mitigation.
      </p>
      
      <div className="bg-blue-50 p-6 rounded-xl mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Intelligent Compliance Engine</h3>
        <ul className="space-y-4">
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Real-time Regulatory Monitoring:</strong> AI-powered surveillance of 50+ government portals and regulatory bodies for instant updates on PF, ESI, PT, TDS, GST, and labor law changes
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Automated Statutory Filings:</strong> One-click generation and submission of PF, ESI, PT, TDS, and other statutory returns with zero manual intervention
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Compliance Intelligence:</strong> Predictive analytics that identify potential compliance risks and recommend corrective actions before deadlines
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Audit-Ready Documentation:</strong> Comprehensive audit trails, version control, and compliance reports for complete transparency and regulatory inspections
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Multi-jurisdiction Support:</strong> Automatic application of state-specific regulations for organizations with pan-India operations
            </div>
          </li>
        </ul>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 p-6 rounded-xl">
          <h4 className="font-semibold text-gray-900 mb-3">Key Benefits</h4>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Eliminates 100% of compliance penalties and late fees</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Reduces payroll processing time by 95%</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Ensures 100% accuracy in salary and tax calculations</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Reduces compliance management workload by 80%</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-xl">
          <h4 className="font-semibold text-gray-900 mb-3">Advanced Payroll Features</h4>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Support for 100+ payroll components and structures</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Multi-bank salary processing with error handling</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Employee self-service for payslips and tax planning</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Real-time compliance alerts and risk mitigation</span>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl mb-8">
        <h4 className="font-semibold text-lg mb-3">Future-Proof Compliance</h4>
        <p className="mb-4">
          Our AI-powered compliance engine doesn't just react to changes—it anticipates them. By analyzing regulatory trends and government announcements, the system prepares organizations for upcoming changes, providing implementation timelines and impact assessments before new regulations take effect.
        </p>
        <div className="grid md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold">24/7</div>
            <div className="text-sm opacity-90">Monitoring</div>
          </div>
          <div>
            <div className="text-2xl font-bold">100%</div>
            <div className="text-sm opacity-90">Compliance</div>
          </div>
          <div>
            <div className="text-2xl font-bold">Zero</div>
            <div className="text-sm opacity-90">Penalties</div>
          </div>
        </div>
      </div>
      
      <p>
        Trusted by over 350 organizations across manufacturing, IT, healthcare, and retail sectors, our Payroll & Compliance Automation system processes more than ₹700 crores in salaries monthly while ensuring 100% compliance with Indian regulations. The platform not only eliminates the risk of penalties and legal issues but also transforms compliance from a cost center into a strategic advantage. With real-time regulatory updates, automated filings, and comprehensive audit trails, our solution provides complete peace of mind while freeing HR teams to focus on strategic initiatives rather than administrative tasks.
      </p>
    </FeatureTemplate>
  );
};

export default PayrollComplianceAutomation;